import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { store } from '../store';
import { useGetPublicMenuQuery, useGetPublicDailyMenuQuery } from '../services/publicMenuApi';
import MenuTemplate from '../components/menuDigital/MenuTemplate';
import '../styles/publicMenu.css';

// Página pública del menú digital (QR). El render vive en MenuTemplate, que
// es la misma pieza que usa el editor en Gestión del Menú — así lo que el
// dueño edita es literalmente lo que ve el comensal. Se monta fuera del
// Provider de la app (ruta pública), así que trae el suyo, igual que
// AutofacturaPortal.

interface PublicMenuPageProps {
  soloHoy?: boolean;
}

const NeutralPage: React.FC<{ titulo: string; detalle: string }> = ({ titulo, detalle }) => (
  <div className="pm-neutral">
    <div>
      <h1>{titulo}</h1>
      <p>{detalle}</p>
    </div>
  </div>
);

const PublicMenuInner: React.FC<PublicMenuPageProps> = ({ soloHoy = false }) => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const carta = useGetPublicMenuQuery(slug ?? '', { skip: !slug || soloHoy });
  const hoy = useGetPublicDailyMenuQuery(slug ?? '', { skip: !slug || !soloHoy });
  const { data, isLoading, error } = soloHoy ? hoy : carta;

  useEffect(() => {
    if (data?.restaurantName) {
      document.title = `${data.restaurantName} · Menú`;
    }
    return () => {
      document.title = 'Massrestaurant';
    };
  }, [data?.restaurantName]);

  // El comensal no viene a descargar nada, así que la página no lleva botón
  // de PDF. El dueño sí lo necesita, y lo lanza desde su configuración: abre
  // esta misma página con ?imprimir=1 y el diálogo de impresión sale solo.
  // Se imprime la página real —no una copia— para que el PDF salga con la
  // plantilla y los colores que eligió.
  const imprimir = searchParams.get('imprimir') === '1';
  const listo = Boolean(data?.available);
  useEffect(() => {
    if (!imprimir || !listo) return;
    // Un cuadro después de pintar: sin esto el diálogo puede abrirse con la
    // hoja a medio renderizar y el PDF sale incompleto.
    const id = window.setTimeout(() => window.print(), 300);
    return () => window.clearTimeout(id);
  }, [imprimir, listo]);

  if (!slug) {
    return <NeutralPage titulo="Menú no encontrado" detalle="Revisa la dirección o vuelve a escanear el QR." />;
  }
  if (isLoading) {
    return <NeutralPage titulo="Cargando el menú…" detalle="Un momento, por favor." />;
  }
  if (error) {
    const status = (error as { status?: number }).status;
    return status === 404 ? (
      <NeutralPage titulo="Menú no encontrado" detalle="Revisa la dirección o vuelve a escanear el QR." />
    ) : (
      <NeutralPage titulo="No pudimos cargar el menú" detalle="Intenta de nuevo en un momento." />
    );
  }
  if (!data) {
    return <NeutralPage titulo="Menú no encontrado" detalle="Revisa la dirección o vuelve a escanear el QR." />;
  }
  if (!data.available) {
    return (
      <NeutralPage
        titulo={data.restaurantName || 'Menú no disponible'}
        detalle="El menú digital no está disponible por el momento. Pregunta en el mostrador."
      />
    );
  }

  return <MenuTemplate menu={data} soloHoy={soloHoy} />;
};

// Wrap with Redux Provider since this is rendered outside the main app's Provider
const PublicMenuPage: React.FC<PublicMenuPageProps> = (props) => (
  <Provider store={store}>
    <PublicMenuInner {...props} />
  </Provider>
);

export default PublicMenuPage;
